import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const PfDeductionDetail = () => {
  const dispatch = useDispatch();

  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // States
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("PayrollMonth", monthName);
      fd.append("PayrollYear", year);

      let records = null;
      let parsedData = null;

      try {
        // Try calling the new dedicated endpoint first
        records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetPFDeductionDetail/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );
      } catch (err) {
        console.warn("Dedicated PFDeductionDetail API failed, falling back to GetPFForm12...", err);
      }

      // Fallback to existing GetPFForm12 API if dedicated is not found or fails
      if (!records || records.length === 0) {
        records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetPFForm12/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );
      }

      if (records && records.length > 0) {
        const firstKey = Object.keys(records[0])[0];
        if (firstKey !== undefined && firstKey !== null) {
          const jsonString = records.map(row => row[firstKey] || "").join("");
          if (jsonString) {
            const rawObj = JSON.parse(jsonString);
            parsedData = rawObj.data || rawObj;
          }
        }
      }
      setReportData(parsedData);
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getRecords = () => {
    const list = reportData?.Records || reportData?.Employees || [];
    return list.map((rec, index) => {
      const sno = rec.Sno || (index + 1);
      const acNo = rec.AcNo || rec.SerialNo || "";
      const name = (rec.EmployeeName || rec.Name || "").toUpperCase();
      const fatherName = (rec.FatherName || "").toUpperCase();
      const eligibleSalary = parseFloat(rec.EligibleSalary || rec.EligibleWages || 0);
      const employeePf = parseFloat(rec.EmployeePF || rec.EmployeePf || rec.PF || 0);
      const employerEpf = parseFloat(rec.EmployerEPF || rec.EmplrAc1 || rec.EmplrPF || 0);
      const employerPension = parseFloat(rec.EmployerPension || rec.EmplrAc10 || rec.Pension || 0);
      const employerTotal = parseFloat(rec.EmployerTotal || (employerEpf + employerPension) || 0);
      const netAmount = parseFloat(rec.NetAmount || (parseFloat(rec.Gross || 0) - employeePf) || 0);

      return {
        sno,
        acNo,
        name,
        fatherName,
        eligibleSalary,
        employeePf,
        employerEpf,
        employerPension,
        employerTotal,
        netAmount
      };
    });
  };

  const allRecords = getRecords();

  const filteredRecords = allRecords.filter(row => {
    const query = searchTerm.toLowerCase();
    return (
      row.name.includes(query) ||
      row.fatherName.includes(query) ||
      row.acNo.toString().includes(query)
    );
  });

  // Calculate totals
  const totalWages = filteredRecords.reduce((sum, r) => sum + r.eligibleSalary, 0);
  const totalEmployeePF = filteredRecords.reduce((sum, r) => sum + r.employeePf, 0);
  const totalEmployerEPF = filteredRecords.reduce((sum, r) => sum + r.employerEpf, 0);
  const totalEmployerPension = filteredRecords.reduce((sum, r) => sum + r.employerPension, 0);
  const totalEmployerTotal = filteredRecords.reduce((sum, r) => sum + r.employerTotal, 0);
  const totalNet = filteredRecords.reduce((sum, r) => sum + r.netAmount, 0);

  // jsPDF Export
  const exportToPDF = () => {
    if (filteredRecords.length === 0) {
      alert("No data available.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    // Header Blocks
    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 12, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text(`EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF ${(selectedMonth || "").toUpperCase()} ${selectedYear}`, 105, 17, { align: "center" });

    pdf.setFont("Helvetica", "bold");
    pdf.text(`CODE NO. : ${getFirmPfNo()}`, 14, 23);

    const body = filteredRecords.map(row => [
      row.sno,
      row.acNo,
      `${row.name}\n${row.fatherName}`,
      row.eligibleSalary.toFixed(2),
      row.employeePf.toFixed(2),
      row.employerEpf.toFixed(2),
      row.employerPension > 0 ? row.employerPension.toFixed(2) : "",
      row.employerTotal.toFixed(2),
      row.netAmount.toFixed(2)
    ]);

    // Push total row
    body.push([
      { content: "Total", colSpan: 3, styles: { fontStyle: "bold", halign: "right" } },
      totalWages.toFixed(2),
      totalEmployeePF.toFixed(2),
      totalEmployerEPF.toFixed(2),
      totalEmployerPension.toFixed(2),
      totalEmployerTotal.toFixed(2),
      totalNet.toFixed(2)
    ]);

    autoTable(pdf, {
      head: [[
        { content: "Sr.\nNo.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
        { content: "AC No", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
        { content: "EMPLOYEE NAME\nFATHER NAME", rowSpan: 2, styles: { valign: "middle" } },
        { content: "ELIGIBLE\nSALARY", rowSpan: 2, styles: { valign: "middle" } },
        { content: "EMPLEE\nP.F.", rowSpan: 2, styles: { valign: "middle" } },
        { content: "EMPLOYER'S SHARE", colSpan: 3, styles: { halign: "center" } },
        { content: "NET\nAMOUNT", rowSpan: 2, styles: { valign: "middle" } }
      ], [
        "EPF", "PENSION", "TOTAL"
      ]],
      body,
      startY: 27,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.05, valign: "middle" },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 15, halign: "center" },
        2: { cellWidth: 45 },
        3: { cellWidth: 22 },
        4: { cellWidth: 18 },
        5: { cellWidth: 18 },
        6: { cellWidth: 18 },
        7: { cellWidth: 18 },
        8: { cellWidth: 22 }
      },
      theme: "grid"
    });

    pdf.save(`PF_Deduction_Detail_${sessionFirm?.Name}_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-pf-deduction-container, #print-pf-deduction-container * {
        visibility: visible !important;
      }
      #print-pf-deduction-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .pf-deduction-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
      }
      .pf-deduction-print-table th, .pf-deduction-print-table td {
        border: 1px solid #000;
        padding: 5px;
        font-size: 8px;
        text-align: left;
        vertical-align: top;
      }
      .pf-deduction-print-table th {
        background: #f2f2f2;
        font-weight: bold;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Deduction Detail" motherMenu="Reports" pageContent="Provident Fund Report" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={2} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, AC No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col lg={2} md={6} className="d-flex gap-2 justify-content-end">
              <Button variant="danger" style={{ height: "38px" }} onClick={exportToPDF}>PDF</Button>
              <Button variant="primary" style={{ height: "38px" }} onClick={() => window.print()}>Print</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** to load the register.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4">
                <div className="text-center">
                  <h4 className="fw-bold mb-1 text-dark fs-15">{sessionFirm?.Name}</h4>
                  <div className="fw-bold text-secondary fs-13">EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}</div>
                  <div className="text-muted fs-12">CODE NO. : <strong>{getFirmPfNo()}</strong></div>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading PF Deduction details...</div>
                  </div>
                ) : filteredRecords.length === 0 ? (
                  <div className="text-center text-muted p-5">No records found.</div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover className="mb-0 text-center" style={{ fontSize: "12px" }}>
                      <thead className="bg-light text-dark">
                        <tr>
                          <th rowSpan={2} style={{ width: "5%", verticalAlign: "middle" }}>Sr. No.</th>
                          <th rowSpan={2} style={{ width: "8%", verticalAlign: "middle" }}>AC No</th>
                          <th rowSpan={2} style={{ width: "25%", verticalAlign: "middle" }}>EMPLOYEE NAME / FATHER NAME</th>
                          <th rowSpan={2} style={{ width: "12%", verticalAlign: "middle" }}>ELIGIBLE SALARY</th>
                          <th rowSpan={2} style={{ width: "12%", verticalAlign: "middle" }}>EMPLEE P.F.</th>
                          <th colSpan={3} style={{ width: "26%" }}>EMPLOYER'S SHARE</th>
                          <th rowSpan={2} style={{ width: "12%", verticalAlign: "middle" }}>NET AMOUNT</th>
                        </tr>
                        <tr>
                          <th>EPF</th>
                          <th>PENSION</th>
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecords.map((row) => (
                          <tr key={row.sno}>
                            <td>{row.sno}</td>
                            <td>{row.acNo}</td>
                            <td className="text-start">
                              <strong>{row.name}</strong><br />
                              <span className="text-muted">{row.fatherName}</span>
                            </td>
                            <td>₹{row.eligibleSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-danger fw-semibold">₹{row.employeePf.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-primary">₹{row.employerEpf.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-warning">₹{row.employerPension.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-success fw-semibold">₹{row.employerTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-dark fw-bold">₹{row.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        <tr className="bg-light fw-bold">
                          <td colSpan={3} className="text-end">Total:</td>
                          <td>₹{totalWages.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-danger">₹{totalEmployeePF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-primary">₹{totalEmployerEPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-warning">₹{totalEmployerPension.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-success">₹{totalEmployerTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-dark">₹{totalNet.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print View Layout */}
          <div id="print-pf-deduction-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px", margin: "4px 0" }}>
              EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}
            </div>
            <div style={{ textAlign: "left", fontSize: "9px", margin: "4px 10px" }}>
              CODE NO. : {getFirmPfNo()}
            </div>

            <table className="pf-deduction-print-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>Sr. No.</th>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>AC No</th>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>EMPLOYEE NAME / FATHER NAME</th>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>ELIGIBLE SALARY</th>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>EMPLEE P.F.</th>
                  <th colSpan={3} style={{ textAlign: "center" }}>EMPLOYER'S SHARE</th>
                  <th rowSpan={2} style={{ verticalAlign: "middle" }}>NET AMOUNT</th>
                </tr>
                <tr>
                  <th>EPF</th>
                  <th>PENSION</th>
                  <th>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(row => (
                  <tr key={row.sno}>
                    <td>{row.sno}</td>
                    <td>{row.acNo}</td>
                    <td>
                      <strong>{row.name}</strong><br />
                      {row.fatherName}
                    </td>
                    <td>{row.eligibleSalary.toFixed(2)}</td>
                    <td>{row.employeePf.toFixed(2)}</td>
                    <td>{row.employerEpf.toFixed(2)}</td>
                    <td>{row.employerPension > 0 ? row.employerPension.toFixed(2) : "0.00"}</td>
                    <td>{row.employerTotal.toFixed(2)}</td>
                    <td>{row.netAmount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: "bold", background: "#eee" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>Total:</td>
                  <td>{totalWages.toFixed(2)}</td>
                  <td>{totalEmployeePF.toFixed(2)}</td>
                  <td>{totalEmployerEPF.toFixed(2)}</td>
                  <td>{totalEmployerPension.toFixed(2)}</td>
                  <td>{totalEmployerTotal.toFixed(2)}</td>
                  <td>{totalNet.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfDeductionDetail;
